import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeftIcon, ChevronRightIcon, ArrowLeftIcon,
  AdjustmentsHorizontalIcon, MagnifyingGlassIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { colors } from '../../config/colors';
import AccessOptions from '../access/AccessOptions';
import HTMLFlipBook from 'react-pageflip';
import { useZoomProtection } from '../../hooks/useZoomProtection';

// PDF.js imports
import * as pdfjsLib from 'pdfjs-dist';

// Set worker source to local file
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const PageFlipReader = React.memo(({ onClose, onBackToWebsite }) => {
  const navigate = useNavigate();
  const { user, hasPurchased, loading } = useAuth();

  // Core state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfDocument, setPdfDocument] = useState(null);
  const [pageImages, setPageImages] = useState({});
  const [renderingPages, setRenderingPages] = useState(new Set());
  const [showSettings, setShowSettings] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [theme, setTheme] = useState('sepia');
  const [retryCount, setRetryCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [showPageJumpPanel, setShowPageJumpPanel] = useState(false);
  const [pageJumpInput, setPageJumpInput] = useState('');
  const [showAccessOptions, setShowAccessOptions] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [flipBookReady, setFlipBookReady] = useState(false);
  const [manualNavigation, setManualNavigation] = useState(false);
  const manualNavigationRef = useRef(false);

  // Zoom protection hook
  const { isZooming, justZoomed, isZoomed } = useZoomProtection();

  // Page flip book ref
  const flipBookRef = useRef(null);
  const isNavigatingRef = useRef(false);

  // Access control with production-ready error handling
  useEffect(() => {
    // Wait for loading to complete before checking auth
    if (loading) {
      return;
    }

    if (!user || !hasPurchased) {
      navigate('/');
      return;
    }
  }, [user, hasPurchased, loading, navigate]);

  // Detect mobile screen size (including tablets)
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1200); // Use mobile view for tablets and smaller screens
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Function to render a PDF page to image
  const renderPageToImage = async (pageNumber) => {
    if (!pdfDocument || pdfDocument.fallback || pageImages[pageNumber] || renderingPages.has(pageNumber)) {
      return;
    }

    try {
      setRenderingPages(prev => new Set([...prev, pageNumber]));

      const page = await pdfDocument.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 1.5 });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      await page.render(renderContext).promise;

      const imageDataUrl = canvas.toDataURL('image/png');
      setPageImages(prev => ({
        ...prev,
        [pageNumber]: imageDataUrl
      }));

      canvas.remove();
    } catch (error) {
      console.error(`Error rendering page ${pageNumber}:`, error);
    } finally {
      setRenderingPages(prev => {
        const newSet = new Set(prev);
        newSet.delete(pageNumber);
        return newSet;
      });
    }
  };

  // Load PDF document
  useEffect(() => {
    const loadPdfDocument = async () => {
      try {
        setIsLoading(true);
        setError('');

        const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

        // Fetch PDF data through backend
        const response = await fetch(`${BACKEND_URL}/api/pdf-content`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          try {
            // Load PDF using PDF.js from our backend proxy (bypasses CORS)
            const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const pdfProxyUrl = `${BACKEND_URL}/api/pdf-proxy`;

            const loadingTask = pdfjsLib.getDocument({
              url: pdfProxyUrl,
              cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/cmaps/`,
              cMapPacked: true,
            });

            const pdf = await loadingTask.promise;
            setPdfDocument(pdf);
            setTotalPages(pdf.numPages);

            // Load reading progress
            const savedProgress = localStorage.getItem('fijoli-reading-progress-pageflip');
            if (savedProgress) {
              const progress = parseInt(savedProgress);
              if (progress > 0 && progress <= pdf.numPages) {
                setCurrentPage(progress);
              }
            }
          } catch (pdfError) {
            // Fallback: create basic page structure without PDF.js
            setTotalPages(data.totalPages || 50);
            setPdfDocument({ fallback: true });

            // Load reading progress for fallback mode
            const savedProgress = localStorage.getItem('fijoli-reading-progress-pageflip');
            if (savedProgress) {
              const progress = parseInt(savedProgress);
              if (progress > 0 && progress <= (data.totalPages || 50)) {
                setCurrentPage(progress);
              }
            }
          }
        } else {
          throw new Error(data.error || 'Failed to load PDF');
        }

      } catch (err) {
        setError(`Failed to load book: ${err.message}`);

        // Retry logic for production
        if (retryCount < 3) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 2000 * (retryCount + 1));
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadPdfDocument();
  }, [navigate, retryCount]);

  // Render pages when PDF is loaded
  useEffect(() => {
    if (pdfDocument && !pdfDocument.fallback) {
      // Render first few pages
      for (let i = 1; i <= Math.min(5, totalPages); i++) {
        renderPageToImage(i);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDocument, totalPages]);

  // Render pages around current page when user navigates
  useEffect(() => {
    if (pdfDocument && !pdfDocument.fallback && currentPage > 0) {
      // Render current page and surrounding pages
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, currentPage + 2);

      for (let i = startPage; i <= endPage; i++) {
        renderPageToImage(i);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pdfDocument, totalPages]);


  // Save reading progress
  useEffect(() => {
    if (currentPage > 0 && totalPages > 0) {
      const progress = Math.round((currentPage / totalPages) * 100);
      setReadingProgress(progress);
      localStorage.setItem('fijoli-reading-progress-pageflip', currentPage.toString());
    }
  }, [currentPage, totalPages]);

  // Sync flip book with current page changes (only when not manually navigating)
  useEffect(() => {
    if (isNavigatingRef.current || manualNavigation) {
      return;
    }

    const syncPage = () => {
      if (flipBookRef.current && flipBookRef.current.pageFlip && typeof flipBookRef.current.pageFlip === 'function' && currentPage > 0) {
        try {
          const pageFlipInstance = flipBookRef.current.pageFlip();
          if (pageFlipInstance && typeof pageFlipInstance.getCurrentPageIndex === 'function') {
            const currentFlipPage = pageFlipInstance.getCurrentPageIndex();
            if (currentFlipPage !== currentPage - 1) {
              pageFlipInstance.flip(currentPage - 1);
            }
          }
        } catch (error) {
          // Silently handle sync errors
        }
      }
    };

    // Add a longer delay to ensure manual navigation completes first
    const timeoutId = setTimeout(syncPage, 1200);
    return () => clearTimeout(timeoutId);
  }, [currentPage, manualNavigation]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      navigate('/');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigate]);

  // Anti-piracy measures
  useEffect(() => {
    // Disable right-click context menu
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable text selection
    const handleSelectStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Block common keyboard shortcuts
    const handleKeyDown = (e) => {
      // Block F12, Ctrl+Shift+I, Ctrl+Shift+C, Ctrl+U, Ctrl+S
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'C')) ||
        (e.ctrlKey && e.key === 'u') ||
        (e.ctrlKey && e.key === 's')
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Screenshot attempt detection (basic)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.warn('Page visibility changed - potential screenshot attempt');
      }
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Navigation functions
  const goToPage = useCallback((pageNumber, skipFlipBook = false) => {
    if (pageNumber < 1 || pageNumber > totalPages) {
      return;
    }

    if (isNavigatingRef.current) {
      return;
    }

    // Set navigation flags
    isNavigatingRef.current = true;
    setManualNavigation(true);
    manualNavigationRef.current = true;

    // Update current page first
    setCurrentPage(pageNumber);

    // Sync with flip book only if not skipping and flip book is ready
    if (!skipFlipBook && flipBookReady && flipBookRef.current && flipBookRef.current.pageFlip && typeof flipBookRef.current.pageFlip === 'function') {
      try {
        const pageFlipInstance = flipBookRef.current.pageFlip();
        if (pageFlipInstance && typeof pageFlipInstance.flip === 'function') {
          // Use a more direct approach - set the page without animation
          pageFlipInstance.turnToPage(pageNumber - 1);
        }
      } catch (error) {
        // Fallback to regular flip if turnToPage doesn't exist
        try {
          const pageFlipInstance = flipBookRef.current.pageFlip();
          if (pageFlipInstance && typeof pageFlipInstance.flip === 'function') {
            pageFlipInstance.flip(pageNumber - 1);
          }
        } catch (fallbackError) {
          // Silently handle flip errors
        }
      }
    }

    // Reset navigation flags after a longer delay to prevent sync interference
    setTimeout(() => {
      isNavigatingRef.current = false;
      setManualNavigation(false);
      manualNavigationRef.current = false;
    }, 1000);
  }, [totalPages, flipBookReady]);

  const nextPage = useCallback(() => {
    if (currentPage < totalPages && !isNavigatingRef.current) {
      // Trigger flip book animation directly
      if (flipBookRef.current && flipBookRef.current.pageFlip && typeof flipBookRef.current.pageFlip === 'function') {
        try {
          const pageFlipInstance = flipBookRef.current.pageFlip();
          if (pageFlipInstance && typeof pageFlipInstance.flipNext === 'function') {
            pageFlipInstance.flipNext();
          }
        } catch (error) {
          // Fallback to direct page change
          setCurrentPage(currentPage + 1);
        }
      } else {
        setCurrentPage(currentPage + 1);
      }
    }
  }, [currentPage, totalPages]);

  const prevPage = useCallback(() => {
    if (currentPage > 1 && !isNavigatingRef.current) {
      // Trigger flip book animation directly
      if (flipBookRef.current && flipBookRef.current.pageFlip && typeof flipBookRef.current.pageFlip === 'function') {
        try {
          const pageFlipInstance = flipBookRef.current.pageFlip();
          if (pageFlipInstance && typeof pageFlipInstance.flipPrev === 'function') {
            pageFlipInstance.flipPrev();
          }
        } catch (error) {
          // Fallback to direct page change
          setCurrentPage(currentPage - 1);
        }
      } else {
        setCurrentPage(currentPage - 1);
      }
    }
  }, [currentPage]);

  // Prevent flipbook events during zoom cooldown
  const handleFlipBookTouch = (e) => {
    if (justZoomed || isZooming) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  // Search functionality
  const searchInPDF = useCallback(async (query) => {
    if (!pdfDocument || !query.trim()) {
      setSearchResults([]);
      setCurrentSearchIndex(0);
      return;
    }

    setIsSearching(true);
    const results = [];

    try {
      // Search through all pages
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const page = await pdfDocument.getPage(pageNum);
        const textContent = await page.getTextContent();

        // Extract text from text content
        const pageText = textContent.items
          .map(item => item.str)
          .join(' ')
          .toLowerCase();

        const queryLower = query.toLowerCase();
        if (pageText.includes(queryLower)) {
          // Find all occurrences on this page
          const regex = new RegExp(queryLower, 'gi');
          let match;
          let matchCount = 0;

          while ((match = regex.exec(pageText)) !== null && matchCount < 10) {
            results.push({
              page: pageNum,
              index: match.index,
              text: match[0],
              context: pageText.substring(Math.max(0, match.index - 30), match.index + query.length + 30)
            });
            matchCount++;
          }
        }
      }

      setSearchResults(results);
      setCurrentSearchIndex(0);

      if (results.length > 0) {
        // Navigate to first result
        goToPage(results[0].page);
      }
    } catch (error) {
      // Handle search error silently in production
    } finally {
      setIsSearching(false);
    }
  }, [pdfDocument, totalPages, goToPage]);

  // Navigate to next/previous search result
  const goToNextResult = useCallback(() => {
    if (searchResults.length === 0) return;

    const nextIndex = (currentSearchIndex + 1) % searchResults.length;
    setCurrentSearchIndex(nextIndex);
    goToPage(searchResults[nextIndex].page);
  }, [searchResults, currentSearchIndex, goToPage]);

  const goToPrevResult = useCallback(() => {
    if (searchResults.length === 0) return;

    const prevIndex = currentSearchIndex === 0 ? searchResults.length - 1 : currentSearchIndex - 1;
    setCurrentSearchIndex(prevIndex);
    goToPage(searchResults[prevIndex].page);
  }, [searchResults, currentSearchIndex, goToPage]);

  // Handle search input changes
  const handleSearchChange = useCallback((e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.trim()) {
      // Debounce search
      const timeoutId = setTimeout(() => {
        searchInPDF(query);
      }, 300);

      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]);
      setCurrentSearchIndex(0);
    }
  }, [searchInPDF]);

  // Page jump functions
  const handlePageJump = useCallback(() => {
    const pageNumber = parseInt(pageJumpInput);
    if (pageNumber && pageNumber >= 1 && pageNumber <= totalPages) {
      goToPage(pageNumber);
      setPageJumpInput('');
      setShowPageJumpPanel(false);
    }
  }, [pageJumpInput, totalPages, goToPage]);

  const handlePageJumpInputChange = useCallback((e) => {
    const value = e.target.value;
    // Only allow numbers
    if (/^\d*$/.test(value)) {
      setPageJumpInput(value);
    }
  }, []);

  const handlePageJumpKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      handlePageJump();
    }
  }, [handlePageJump]);

  // Theme switching
  const switchTheme = useCallback((newTheme) => {
    setTheme(newTheme);
  }, []);

  // Get current theme colors
  const currentTheme = colors.themes[theme] || colors.themes.light;

  // Anti-piracy: Generate user-specific watermark
  const generateWatermark = useCallback(() => {
    // Use www.fijoli.com instead of email for watermark
    const timestamp = new Date().toISOString().split('T')[0];
    const watermark = `www.fijoli.com | ${timestamp}`;
    return watermark;
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.target.tagName === 'INPUT') {
        return; // Don't interfere with input fields
      }

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          prevPage();
          break;
        case 'ArrowRight':
          e.preventDefault();
          nextPage();
          break;
        case 'f':
        case 'F':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            setShowSearchPanel(!showSearchPanel);
          }
          break;
        case 'Escape':
          setShowSettings(false);
          setShowSearchPanel(false);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [prevPage, nextPage, showSearchPanel]);



  // Render individual page component for react-pageflip
  const renderPage = (pageNumber) => {
    // Render cover page for page 1
    // if (pageNumber === 1) {
    //   return renderCoverPage();
    // }

    // // Render back cover for last page
    // if (pageNumber === totalPages) {
    //   return renderBackCover();
    // }

    // Check if we have a rendered image for this page
    const pageImage = pageImages[pageNumber];
    const isRendering = renderingPages.has(pageNumber);

    return (
      <div className="page" style={{
        width: '100%',
        height: '100%',
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        // padding: '20px',
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)`
      }}>
        {/* Page content placeholder */}
        {pageImage ? (
          // Show rendered PDF page
          <img
            src={pageImage}
            alt={`Page ${pageNumber}`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
            }}
          />
        ) : isRendering ? (
          // Show loading state
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
            <p className="text-lg opacity-70" style={{ color: currentTheme.text }}>
              Loading page {pageNumber}...
            </p>
          </div>
        ) : (
          // Show placeholder content
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            position: 'relative'
          }}>
            {/* Page header */}
            <div className="mb-8">
              <BookOpenIcon className="h-20 w-20 mx-auto mb-4 opacity-60" style={{ color: currentTheme.text }} />
              <h2 className="text-3xl font-bold mb-2" style={{ color: currentTheme.text }}>
                Fijoli
              </h2>
              <p className="text-lg opacity-70" style={{ color: currentTheme.text }}>
                Page {pageNumber}
              </p>
            </div>

            {/* Sample content */}
            <div className="max-w-md mx-auto text-left space-y-4" style={{ color: currentTheme.text }}>
              <p className="text-sm leading-relaxed">
                This is a sample page from the Fijoli book. The content will be dynamically loaded from your PDF or Google Docs source.
              </p>
              <p className="text-sm leading-relaxed">
                The Page Flip Reader provides an immersive reading experience with realistic page turning animations and book-like navigation.
              </p>
              <div className="mt-8 p-4 rounded-lg" style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}>
                <p className="text-xs text-center opacity-60">
                  Use arrow keys or click the navigation buttons to turn pages
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Page number */}
        {/* <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-lg border border-gray-200">
          <span className="text-sm font-medium text-gray-700">{pageNumber}</span>
        </div> */}

        {/* Anti-piracy watermark */}
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 pointer-events-none z-50">
          <div
            className="text-sm select-none"
            style={{
              color: '#666666',
              opacity: 0.6,
              textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
              fontSize: '6px',
              letterSpacing: '0.5px'
            }}
          >
            {generateWatermark()}
          </div>
        </div>

        {/* Page edge effect */}
        <div className="absolute top-0 right-0 w-2 h-full bg-gradient-to-l from-gray-300/40 to-transparent"></div>
        <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-r from-gray-300/40 to-transparent"></div>
      </div>
    );
  };

  // Loading state for authentication
  if (loading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-red-800 text-lg font-semibold">Checking authentication...</p>
          <p className="text-red-600 text-sm mt-2">Please wait while we verify your access</p>
        </div>
      </div>
    );
  }

  // Loading state for PDF
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-red-800 text-lg font-semibold">Opening your book...</p>
          <p className="text-red-600 text-sm mt-2">Please wait while we load your reading experience</p>
          {retryCount > 0 && (
            <p className="text-red-600 text-sm mt-2">Retry attempt {retryCount}/3</p>
          )}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-600 text-6xl mb-4">📖</div>
          <h2 className="text-2xl font-bold text-red-800 mb-2">Unable to Load Book</h2>
          <p className="text-red-700 mb-4">{error}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {retryCount < 3 && (
              <button
                onClick={() => {
                  setError('');
                  setIsLoading(true);
                  setRetryCount(prev => prev + 1);
                }}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            )}
            <button
              onClick={() => {
                if (onClose) onClose();
                else if (onBackToWebsite) onBackToWebsite();
                else navigate('/');
              }}
              className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="pageflip-reader-container fixed inset-0 overflow-hidden"
      style={{
        background: currentTheme.background
      }}
    >
      {/* Mobile-specific CSS */}
      <style jsx>{`
        .pageflip-reader-container {
          touch-action: manipulation pinch-zoom;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          -webkit-touch-callout: none;
          -webkit-tap-highlight-color: transparent;
        }
        
        /* Ensure pinch zoom works on all child elements */
        .pageflip-reader-container * {
          touch-action: manipulation pinch-zoom !important;
        }
        
        /* Override HTMLFlipBook touch handling */
        .pageflip-reader-container .stf__parent,
        .pageflip-reader-container .stf__item,
        .pageflip-reader-container .stf__block {
          touch-action: manipulation pinch-zoom !important;
          pointer-events: auto !important;
        }
        
        /* Force pinch zoom on the flipbook container */
        .pageflip-reader-container .stf__parent {
          touch-action: manipulation pinch-zoom !important;
          -webkit-touch-callout: default !important;
        }
        
        /* Zoom state styles */
        .zooming {
          touch-action: pinch-zoom !important;
        }
        
        .zoom-cooldown {
          pointer-events: none !important;
          transition: pointer-events 0.3s ease;
        }

        .pageflip-reader-container * {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          -webkit-touch-callout: none;
        }

        /* Allow text selection only in input fields */
        .pageflip-reader-container input,
        .pageflip-reader-container textarea {
          -webkit-user-select: text;
          -moz-user-select: text;
          -ms-user-select: text;
          user-select: text;
        }

        @media (max-width: 768px) {
          button {
            min-height: 44px;
            min-width: 44px;
            touch-action: manipulation;
          }
          
          .settings-panel,
          .search-panel {
            touch-action: manipulation;
          }
          
          .settings-panel button,
          .search-panel button {
            touch-action: manipulation;
          }
        }
      `}</style>

      {/* Top Navigation Bar */}
      <div
        className={`relative z-40 h-16 flex items-center justify-between px-2 sm:px-4 shadow-lg backdrop-blur-sm ${isMobile ? 'text-sm' : ''}`}
        style={{
          backgroundColor: `${currentTheme.surface}CC`,
          borderBottom: `2px solid ${currentTheme.border}`,
          backgroundImage: 'linear-gradient(to right, rgba(139, 69, 19, 0.1), rgba(160, 82, 45, 0.1))'
        }}
      >
        {/* Left side - Back and Title */}
        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              if (onClose) {
                onClose();
              } else if (onBackToWebsite) {
                onBackToWebsite();
              } else {
                navigate('/');
              }
            }}
            className="p-2 rounded-lg hover:bg-opacity-20 transition-colors"
            style={{ color: currentTheme.text, backgroundColor: `${colors.primary.main}20` }}
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className={`text-lg font-bold truncate ${isMobile ? 'hidden' : ''}`} style={{ color: currentTheme.text }}>
              Fijoli Page Flip Reader
            </h1>
            <p className={`text-sm opacity-70 truncate ${isMobile ? 'hidden' : ''}`} style={{ color: currentTheme.text }}>
              Page {currentPage} of {totalPages} ({readingProgress}% complete)
            </p>
          </div>
        </div>

        {/* Center - Page Navigation */}
        <div className={`flex items-center ${isMobile ? 'space-x-1' : 'space-x-2'}`}>
          {!isMobile && (
            <motion.button
              onClick={prevPage}
              disabled={currentPage <= 1}
              whileHover={{ scale: currentPage > 1 ? 1.1 : 1 }}
              whileTap={{ scale: currentPage > 1 ? 0.95 : 1 }}
              className="p-2 rounded-lg transition-all duration-200 disabled:opacity-50"
              style={{
                color: currentPage > 1 ? '#ffffff' : currentTheme.text,
                backgroundColor: currentPage > 1 ? colors.primary.main : 'rgba(107, 114, 128, 0.3)'
              }}
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </motion.button>
          )}

          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium truncate" style={{ color: currentTheme.text }}>
              Page {currentPage} of {totalPages}
              {isZoomed && <span className="ml-2 text-xs opacity-70">🔍 Zoomed</span>}
            </span>
            <button
              onClick={() => setShowPageJumpPanel(true)}
              className="px-3 py-1 rounded text-sm transition-colors bg-amber-50 hover:bg-amber-100"
              style={{ color: '#92400e' }}
              title="Go to specific page"
            >
              Go to Page
            </button>
          </div>

          {!isMobile && (
            <motion.button
              onClick={nextPage}
              disabled={currentPage >= totalPages}
              whileHover={{ scale: currentPage < totalPages ? 1.1 : 1 }}
              whileTap={{ scale: currentPage < totalPages ? 0.95 : 1 }}
              className="p-2 rounded-lg transition-all duration-200 disabled:opacity-50"
              style={{
                color: currentPage < totalPages ? '#ffffff' : currentTheme.text,
                backgroundColor: currentPage < totalPages ? colors.primary.main : 'rgba(107, 114, 128, 0.3)'
              }}
            >
              <ChevronRightIcon className="h-5 w-5" />
            </motion.button>
          )}
        </div>

        {/* Right side - Settings, Search, and Order Hard Copy */}
        <div className="flex items-center space-x-2">
          {/* Order Hard Copy Button */}
          {user && hasPurchased && !user.hasHardCopy && !user.hasUpgraded && (
            <motion.button
              onClick={() => setShowAccessOptions(true)}
              className="px-4 py-2 rounded-lg transition-all duration-200 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Order Physical Book"
            >
              📖 Hard Copy
            </motion.button>
          )}

          <button
            onClick={() => setShowSearchPanel(!showSearchPanel)}
            className="p-2 rounded-lg hover:bg-opacity-20 transition-colors"
            style={{ color: currentTheme.text, backgroundColor: `${colors.primary.main}20` }}
            title="Search"
          >
            <MagnifyingGlassIcon className="h-5 w-5" />
          </button>

          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 rounded-lg hover:bg-opacity-20 transition-colors"
            style={{ color: currentTheme.text, backgroundColor: `${colors.primary.main}20` }}
            title="Settings"
          >
            <AdjustmentsHorizontalIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Side Navigation Arrows - Desktop and Mobile */}
      <div className="fixed left-1 top-1/2 transform -translate-y-1/2 z-40">
        <motion.button
          onClick={prevPage}
          disabled={currentPage <= 1}
          whileTap={{ scale: currentPage > 1 ? 0.95 : 1 }}
          className="p-2 transition-all duration-200 disabled:opacity-30 border-0 outline-none focus:outline-none"
          style={{
            color: currentPage > 1 ? colors.primary.main : currentTheme.text,
            backgroundColor: 'transparent',
            border: 'none',
            outline: 'none'
          }}
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </motion.button>
      </div>

      <div className="fixed right-1 top-1/2 transform -translate-y-1/2 z-40">
        <motion.button
          onClick={nextPage}
          disabled={currentPage >= totalPages}
          whileTap={{ scale: currentPage < totalPages ? 0.95 : 1 }}
          className="p-2 transition-all duration-200 disabled:opacity-30 border-0 outline-none focus:outline-none"
          style={{
            color: currentPage < totalPages ? colors.primary.main : currentTheme.text,
            backgroundColor: 'transparent',
            border: 'none',
            outline: 'none'
          }}
        >
          <ChevronRightIcon className="h-5 w-5" />
        </motion.button>
      </div>

      {/* Main Book Content with Page Flip */}
      <div className="h-full flex items-center justify-center">
        <div
          className={`w-full h-full ${isMobile ? 'max-w-full' : 'max-w-6xl'} ${isZooming ? 'zooming' : ''} ${justZoomed ? 'zoom-cooldown' : ''}`}
          style={{
            touchAction: isZooming ? 'pinch-zoom' : 'pan-x pan-y pinch-zoom',
            pointerEvents: justZoomed ? 'none' : 'auto'
          }}
        >
          <HTMLFlipBook
            ref={flipBookRef}
            width={isMobile ? 350 : 400}
            height={isMobile ? 500 : 600}
            size="stretch"
            minWidth={isMobile ? 300 : 300}
            maxWidth={isMobile ? 400 : 800}
            minHeight={isMobile ? 400 : 400}
            maxHeight={isMobile ? 500 : 900}
            maxShadowOpacity={0.5}
            showCover={true}
            mobileScrollSupport={true}
            usePortrait={true}
            disableFlipByClick={false}
            swipeDistance={60}
            useMouseEvents={false}
            clickEventForward={false}
            onTouchStart={handleFlipBookTouch}
            onTouchMove={handleFlipBookTouch}
            onTouchEnd={handleFlipBookTouch}
            onFlip={(e) => {
              // Only update current page if not manually navigating
              if (!isNavigatingRef.current && !manualNavigationRef.current) {
                isNavigatingRef.current = true;
                setCurrentPage(e.data + 1);
                setTimeout(() => {
                  isNavigatingRef.current = false;
                }, 100);
              } else {
                // If we're manually navigating, just update the ref without changing state
                console.log('onFlip blocked during manual navigation');
              }
            }}
            onInit={(e) => {
              // Mark flip book as ready
              setFlipBookReady(true);

              // Initialize to current page with a small delay to ensure flip book is ready
              setTimeout(() => {
                if (flipBookRef.current && flipBookRef.current.pageFlip && typeof flipBookRef.current.pageFlip === 'function' && currentPage > 0) {
                  try {
                    const pageFlipInstance = flipBookRef.current.pageFlip();
                    if (pageFlipInstance && typeof pageFlipInstance.flip === 'function') {
                      pageFlipInstance.flip(currentPage - 1);
                    }
                  } catch (error) {
                    // Silently handle initialization errors
                  }
                }
              }, 200);
            }}
            className="mx-auto"
            style={{
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              borderRadius: '8px',
              touchAction: 'pan-x pan-y pinch-zoom'
            }}
          >
            {/* Generate pages dynamically */}
            {Array.from({ length: totalPages }, (_, index) => (
              <div key={index} className="page">
                {renderPage(index + 1)}
              </div>
            ))}
          </HTMLFlipBook>
        </div>
      </div>

      {/* Settings Panel */}
      <AnimatePresence>
        {showSettings && (
          <>
            <motion.div
              className="fixed inset-0 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowSettings(false)}
            />
            <motion.div
              className="absolute top-16 right-4 bg-white rounded-lg shadow-xl p-4 w-64 z-50"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold mb-4" style={{ color: currentTheme.text }}>Settings</h3>

              {/* Theme Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.text }}>Theme</label>
                <div className="flex space-x-2">
                  {['light', 'dark', 'sepia'].map((themeOption) => (
                    <button
                      key={themeOption}
                      onClick={() => switchTheme(themeOption)}
                      className={`px-3 py-1 rounded text-sm transition-colors ${theme === themeOption ? 'ring-2' : ''
                        }`}
                      style={{
                        backgroundColor: theme === themeOption ? colors.primary.main : currentTheme.input.background,
                        color: theme === themeOption ? 'white' : currentTheme.text,
                        borderColor: currentTheme.border
                      }}
                    >
                      {themeOption.charAt(0).toUpperCase() + themeOption.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Search Panel */}
      <AnimatePresence>
        {showSearchPanel && (
          <>
            <motion.div
              className="fixed inset-0 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowSearchPanel(false)}
            />
            <motion.div
              className="absolute top-16 right-4 bg-white rounded-lg shadow-xl p-4 w-80 z-50"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold mb-4" style={{ color: currentTheme.text }}>Search</h3>

              {/* Search Input */}
              <div className="relative mb-4">
                <div className="flex">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyPress={(e) => e.key === 'Enter' && searchInPDF(searchQuery)}
                    placeholder="Search in book..."
                    className="flex-1 px-3 py-2 rounded-l border"
                    style={{
                      backgroundColor: currentTheme.input.background,
                      color: currentTheme.input.text,
                      borderColor: currentTheme.input.border,
                      fontSize: '16px', // Prevent zoom on mobile
                      WebkitAppearance: 'none',
                      borderRadius: '0'
                    }}
                  />
                  <button
                    onClick={() => searchInPDF(searchQuery)}
                    disabled={!searchQuery.trim() || isSearching}
                    className="px-4 py-2 rounded-r border-l-0 border text-sm transition-colors disabled:opacity-50"
                    style={{
                      backgroundColor: colors.primary.main,
                      color: 'white',
                      borderColor: currentTheme.accent
                    }}
                  >
                    {isSearching ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      '🔍'
                    )}
                  </button>
                </div>
                {isSearching && (
                  <div className="absolute right-12 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderColor: currentTheme.accent }}></div>
                  </div>
                )}
              </div>

              {/* Search Navigation */}
              {searchResults.length > 0 && (
                <div className="flex items-center space-x-1 mb-4">
                  <button
                    onClick={goToPrevResult}
                    disabled={searchResults.length === 0}
                    className="p-2 rounded hover:bg-opacity-20 disabled:opacity-50 transition-colors"
                    style={{ color: currentTheme.text, backgroundColor: `${colors.primary.main}20` }}
                    title="Previous Result"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  <span className="text-sm px-2" style={{ color: currentTheme.text }}>
                    {currentSearchIndex + 1} of {searchResults.length}
                  </span>

                  <button
                    onClick={goToNextResult}
                    disabled={searchResults.length === 0}
                    className="p-2 rounded hover:bg-opacity-20 disabled:opacity-50 transition-colors"
                    style={{ color: currentTheme.text, backgroundColor: `${colors.primary.main}20` }}
                    title="Next Result"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="max-h-64 overflow-y-auto">
                  <h4 className="text-sm font-medium mb-2" style={{ color: currentTheme.text }}>
                    Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}:
                  </h4>
                  <div className="space-y-2">
                    {searchResults.map((result, index) => (
                      <div
                        key={`${result.page}-${index}`}
                        className={`p-3 rounded border cursor-pointer transition-colors ${index === currentSearchIndex ? 'ring-2' : ''
                          }`}
                        style={{
                          backgroundColor: index === currentSearchIndex ? `${colors.primary.main}20` : currentTheme.input.background,
                          borderColor: index === currentSearchIndex ? colors.primary.main : currentTheme.input.border,
                          color: currentTheme.text
                        }}
                        onClick={() => {
                          setCurrentSearchIndex(index);
                          goToPage(result.page);
                        }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium" style={{ color: currentTheme.accent }}>
                            Page {result.page}
                          </span>
                          <span className="text-xs opacity-70">
                            Result {index + 1}
                          </span>
                        </div>
                        <p className="text-sm opacity-80">
                          ...{result.context}...
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Results */}
              {searchQuery && !isSearching && searchResults.length === 0 && (
                <div className="text-center py-4">
                  <div className="text-4xl mb-2">🔍</div>
                  <p className="text-sm opacity-70" style={{ color: currentTheme.text }}>
                    No results found for "{searchQuery}"
                  </p>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Page Jump Panel */}
      <AnimatePresence>
        {showPageJumpPanel && (
          <>
            <motion.div
              className="fixed inset-0 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowPageJumpPanel(false)}
            />
            <motion.div
              className="absolute top-16 right-4 bg-white rounded-lg shadow-xl p-4 w-80 z-50"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold mb-4" style={{ color: currentTheme.text }}>Go to Page</h3>

              {/* Page Jump Input */}
              <div className="relative mb-4">
                <div className="flex">
                  <input
                    type="text"
                    value={pageJumpInput}
                    onChange={handlePageJumpInputChange}
                    onKeyPress={handlePageJumpKeyPress}
                    placeholder={`1-${totalPages}`}
                    className="flex-1 px-3 py-2 rounded-l border"
                    style={{
                      backgroundColor: currentTheme.input.background,
                      color: currentTheme.input.text,
                      borderColor: currentTheme.input.border,
                      fontSize: '16px', // Prevent zoom on mobile
                      WebkitAppearance: 'none',
                      borderRadius: '0'
                    }}
                  />
                  <button
                    onClick={handlePageJump}
                    disabled={!pageJumpInput.trim()}
                    className="px-4 py-2 rounded-r border-l-0 border text-sm transition-colors disabled:opacity-50"
                    style={{
                      backgroundColor: colors.primary.main,
                      color: 'white',
                      borderColor: currentTheme.accent
                    }}
                  >
                    Go
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter a page number between 1 and {totalPages}
                </p>
              </div>


              {/* Close Button */}
              <button
                onClick={() => setShowPageJumpPanel(false)}
                className="w-full px-4 py-2 rounded text-sm transition-colors"
                style={{
                  backgroundColor: currentTheme.input.background,
                  color: currentTheme.text,
                  borderColor: currentTheme.border
                }}
              >
                Cancel
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Access Options Modal */}
      <AccessOptions
        isOpen={showAccessOptions}
        onClose={() => setShowAccessOptions(false)}
        onAccessGranted={() => {
          setShowAccessOptions(false);
        }}
      />

      {/* Anti-piracy: Copyright Notice */}
      <div className="fixed top-2 left-1/2 transform -translate-x-1/2 z-30 hidden md:block">
        <div className="text-center">
          <p className="text-xs opacity-60 text-gray-600 bg-white px-3 py-1 rounded-full shadow-sm">
            © Fijoli - All rights reserved. Unauthorized copying prohibited.
          </p>
        </div>
      </div>
    </div>
  );
});

export default PageFlipReader;
